import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';

import { searchPropertyByName } from 'services/addProperty.service';
import { searchUserByName } from 'services/users.service';

const AsyncDropdown = ({
  dropdownId,
  optionLabel,
  optionValue,
  inputLabel,
  onChange,
}) => {
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function fetchProperties(searchVal) {
    try {
      setIsLoading(true);

      switch (dropdownId) {
        case 'propertyName': {
          const response = await searchPropertyByName(searchVal);
          setOptions([...(response?.data?.data?.properties ?? [])]);
          break;
        }

        case 'userName': {
          const response = await searchUserByName(searchVal);
          setOptions([...(response?.data?.data?.userList ?? [])]);
          break;
        }

        default:
          break;
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  }

  const debouncedLoadData = useCallback(debounce(fetchProperties, 1000), []);

  const handleChange = (_, val) => {
    setValue(val);
    onChange(val);
  };

  const onInputChange = (e, val, reason) => {
    if (reason === 'reset') return;
    if (reason === 'clear' || !val?.length) {
      setOptions([]);
      return;
    }
    if (reason === 'input' && !val?.length) {
      setOptions([]);
    }
    setSearchQuery(val ?? '');
    // debouncedLoadData(val);
  };

  const getOptionLabel = (option) => String(option[optionLabel]);

  const filterOptions = (option) => option;

  useEffect(() => {
    searchQuery?.length && debouncedLoadData(searchQuery);

    return () => {
      debouncedLoadData.cancel();
    };
  }, [searchQuery]);

  return (
    <Autocomplete
      autoComplete
      options={options}
      value={value}
      clearOnBlur={false}
      disablePortal
      onChange={handleChange}
      onInputChange={onInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) =>
        option[optionValue] === value[optionValue]
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputLabel}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      loading={isLoading}
      filterOptions={filterOptions}
      blurOnSelect={true}
      id={dropdownId}
      renderOption={(props, option) => {
        return (
          <Box component="li" {...props} key={option[optionValue]}>
            {option[optionLabel]}
          </Box>
        );
      }}
      noOptionsText={
        searchQuery?.length > 0 ? 'No options' : `Type to Search ${inputLabel}`
      }
    />
  );
};

AsyncDropdown.propTypes = {
  dropdownId: PropTypes.oneOf(['propertyName', 'userName']),
  optionLabel: PropTypes.string.isRequired,
  optionValue: PropTypes.string.isRequired,
  inputLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};

AsyncDropdown.defaultProps = {
  dropdownId: '',
  onChange: () => {},
};

export default AsyncDropdown;
